<script setup>
import api from "../api/api"
import Swal from "sweetalert2"
import BaseInput from "./UI/BaseInput.vue"
import BaseButton from "./UI/BaseButton.vue"
</script>

<!-- Alert Routing (A01) — ported from Numerica (numerica-p2-frontend/src/components/AlertRouting.vue) -->
<template>
  <div class="body-container">
    <header>
      <h2>Alert Routing</h2>
      <h2>A01</h2>
    </header>
    <div class="table-container">
      <table id="table">
        <thead>
          <tr>
            <th>Num</th>
            <th>Alert Type</th>
            <th>Symitar Warning Codes</th>
            <th>Routing</th>
            <th>Routing Description</th>
            <th>Ranking</th>
            <th>Action</th>
          </tr>
          <tr>
            <th class="table-head-1"></th>
            <th class="table-head-1">
              <BaseInput type="text" v-model="newAlert.alertType" placeholder="e.g. Fraud Hold" />
            </th>
            <th class="table-head-1">
              <BaseInput type="text" v-model="newAlert.symitarWarningCodes" placeholder="e.g. 4,17" />
            </th>
            <th class="table-head-1">
              <BaseInput type="text" v-model="newAlert.routingCode" :disabled="editingAlert" maxlength="10"
                :title="editingAlert ? 'Routing code cannot be changed — delete and re-add to change it' : ''" />
            </th>
            <th class="table-head-1">
              <BaseInput type="text" v-model="newAlert.routingDescription" />
            </th>
            <th class="table-head-1">
              <BaseInput type="text" v-model="newAlert.ranking" />
            </th>
            <th class="table-head-1">
              <div v-if="editingAlert" style="display: flex; justify-content: space-evenly; align-items: center;">
                <BaseButton @click="onClickCancel" class="btn-cancel" style="flex: 1;">
                  Cancel
                </BaseButton>
                <span style="margin: 0 6px"></span>
                <BaseButton @click="onClickUpdate" class="btn-submit" style="flex: 1;">
                  <i class="fa-solid fa-check"></i>
                  Update
                </BaseButton>
              </div>
              <div v-else>
                <BaseButton @click="onClickSave" class="btn-submit">
                  <i class="fa-solid fa-check"></i> Save
                </BaseButton>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!alertList.length">
            <td colspan="7" style="text-align: center; color: #888;">No alert routing set up yet.</td>
          </tr>
          <tr v-for="(alert, index) in alertList" :key="alert.alertTypeId"
            :class="{ 'row-editing': editingAlert && newAlert.alertTypeId === alert.alertTypeId }">
            <td style="text-align: right;">{{ index + 1 }}</td>
            <td>{{ alert.alertType }}</td>
            <td>{{ alert.symitarWarningCodes }}</td>
            <td>{{ alert.routingCode }}</td>
            <td>{{ alert.routingDescription }}</td>
            <td>{{ alert.ranking }}</td>
            <td>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <BaseButton
                  @click.stop="onClickDelete(alert)"
                  class="btn-danger"
                  style="cursor: pointer; flex: 1;"
                >
                  <i class="fa-solid fa-trash"></i>
                  Delete
                </BaseButton>
                <span style="margin: 0 6px"></span>
                <BaseButton
                  @click.stop="onClickEdit(alert)"
                  class="btn-submit"
                  style="cursor: pointer; flex: 1;"
                >
                  <i class="fa-solid fa-pencil"></i>
                  Edit
                </BaseButton>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "AlertRouting",
  props: ["brandData"],
  data: () => ({
    company: localStorage.getItem("companyID"),
    alertList: [],
    newAlert: {},
    editingAlert: false,
  }),
  async mounted() {
    await this.getAlertRouting();
  },
  methods: {
    async getAlertRouting() {
      try {
        const response = await api.get(`alert-routing?company=${this.company}`);
        this.alertList = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.log(error);
      }
    },
    invalidFields() {
      const blank = (v) => v === undefined || v === null || String(v).trim() === "";
      return blank(this.newAlert.alertType) ||
        blank(this.newAlert.symitarWarningCodes) ||
        blank(this.newAlert.routingCode) ||
        blank(this.newAlert.routingDescription) ||
        blank(this.newAlert.ranking);
    },
    showErrorDialog(error) {
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.error || "Something went wrong";
        Swal.fire({ title: status === 400 ? "Input Error" : "Error", text: message, icon: "error" });
      } else {
        Swal.fire({ title: "Network Error", text: "Unable to connect to server", icon: "error" });
      }
    },
    showNewAlertRoutingDialog(isNewRoute) {
      if (isNewRoute) {
        Swal.fire({
          title: "New Routing",
          text: "Note: New routing saved. Please inform the Architect Administrator to update the architect flow.",
          icon: "info",
        });
      }
    },
    async onClickSave() {
      if (this.invalidFields()) {
        Swal.fire({
          title: "Input Error",
          text: "One or more fields are invalid. Please fill out all fields to save.",
          icon: "error",
        });
        return;
      }

      const isNewRoute = !this.alertList.some(item => item.routingCode === String(this.newAlert.routingCode).trim());
      try {
        const response = await api.post(`alert-routing`, { ...this.newAlert, company: this.company });
        if (response.status !== 201) return;
        this.showNewAlertRoutingDialog(isNewRoute);
        this.newAlert = {};
        await this.getAlertRouting();
      } catch (error) {
        this.showErrorDialog(error);
        console.log(error);
      }
    },
    onClickEdit(alert) {
      this.newAlert = JSON.parse(JSON.stringify(alert));
      this.editingAlert = true;
    },
    async onClickUpdate() {
      if (this.invalidFields()) {
        Swal.fire({
          title: "Input Error",
          text: "One or more fields are invalid. Please fill out all fields to save.",
          icon: "error",
        });
        return;
      }
      try {
        const response = await api.put(`alert-routing/${this.newAlert.alertTypeId}`, {
          ...this.newAlert,
          company: this.company,
        });
        if (response.status !== 200) return;
        this.newAlert = {};
        this.editingAlert = false;
        await this.getAlertRouting();
      } catch (error) {
        this.showErrorDialog(error);
        console.log(error);
      }
    },
    onClickCancel() {
      this.newAlert = {};
      this.editingAlert = false;
    },
    async onClickDelete(alert) {
      this.newAlert = {};
      this.editingAlert = false;
      const userResponse = await Swal.fire({
        title: "",
        html: `<p>Are you sure you want to delete alert type: <b>${String(alert.alertType).replace(/</g, "&lt;")}</b></p>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Delete",
      });
      if (!userResponse.isConfirmed) return;

      try {
        const response = await api.delete(`alert-routing/${alert.alertTypeId}?company=${this.company}`);
        if (response.status !== 204) return;
        await this.getAlertRouting();
      } catch (error) {
        this.showErrorDialog(error);
        console.log(error);
      }
    },
  },
}
</script>

<style scoped>
.body-container {
  align-self: unset;
  min-width: 1200px;
}

header {
  display: flex;
  justify-content: space-between;
}

.table-container {
  margin: 1.5rem 0;
}

table {
  width: 100%;
}

button {
  padding: 0.375rem 0.75rem;
}

.btn-danger {
  color: #fff;
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-cancel {
  margin-left: 0;
}

.table-head-1 {
  position: sticky;
  top: 40px;
  background-color: #fff !important;
}

.row-editing td {
  background: #fffbe6;
}

#table tr th:nth-of-type(1) { width: 5%; }
#table tr th:nth-of-type(2) { width: 20%; }
#table tr th:nth-of-type(3) { width: 20%; }
#table tr th:nth-of-type(4) { width: 10%; }
#table tr th:nth-of-type(5) { width: 20%; }
#table tr th:nth-of-type(6) { width: 10%; }
#table tr th:nth-of-type(7) { width: 15%; }
</style>
